/**
 * TransferModal — Crear transferencia entre bodegas.
 * Seleccionar bodega origen, destino, y productos con cantidades.
 */
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2, Plus, Trash2, ArrowRight, Truck } from 'lucide-react'
import { inputCls, labelCls, errorCls } from '@/lib/formStyles'
import { useWarehouses, useWarehouseStock, type Warehouse } from '../hooks/useInventory'
import { TextField } from '@/components/ds/TextField'
import { Modal } from '@/components/ds/Modal'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
}

interface TransferLine {
  product_id: string
  product_sku: string
  product_name: string
  quantity: string
  available: string
}

export default function TransferModal({ open, onClose, onSubmit, isPending }: Props) {
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm()
  const [lines, setLines] = useState<TransferLine[]>([])

  const { data: whData } = useWarehouses({ active: 'true', page_size: 9999 })
  const warehouses = whData?.warehouses || []

  const fromWarehouseId = watch('from_warehouse')
  const { data: stockData } = useWarehouseStock(fromWarehouseId || '', { page_size: 9999 })
  const availableStock = stockData?.stock || []

  useEffect(() => {
    if (open) {
      reset({ from_warehouse: '', to_warehouse: '', notes: '' })
      setLines([])
    }
  }, [open, reset])

  const addLine = () => {
    setLines(prev => [...prev, { product_id: '', product_sku: '', product_name: '', quantity: '', available: '0' }])
  }

  const removeLine = (idx: number) => {
    setLines(prev => prev.filter((_, i) => i !== idx))
  }

  const updateLine = (idx: number, field: string, value: string) => {
    setLines(prev => prev.map((line, i) => {
      if (i !== idx) return line
      if (field === 'product_id') {
        const stock = availableStock.find(s => s.product_id === value)
        return {
          ...line,
          product_id: value,
          product_sku: stock?.product_sku || '',
          product_name: stock?.product_name || '',
          available: stock?.available_quantity || '0',
        }
      }
      return { ...line, [field]: value }
    }))
  }

  const handleFormSubmit = (formData: any) => {
    if (lines.length === 0) return
    const validLines = lines.filter(l => l.product_id && parseFloat(l.quantity) > 0)
    if (validLines.length === 0) return

    onSubmit({
      from_warehouse: formData.from_warehouse,
      to_warehouse: formData.to_warehouse,
      notes: formData.notes || '',
      lines: validLines.map(l => ({
        product_id: l.product_id,
        product_sku: l.product_sku,
        product_name: l.product_name,
        quantity: l.quantity,
      })),
      // Datos de transporte para Guía de Despacho
      transport_data: {
        carrier_name: formData.carrier_name,
        carrier_tax_id: formData.carrier_tax_id || '',
        driver_name: formData.driver_name,
        vehicle_plate: (formData.vehicle_plate || '').toUpperCase(),
      },
    })
  }

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [open, onClose])

  if (!open) return null

  return (
    <Modal open onClose={onClose} title="Nueva transferencia" size="2xl">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-3">
        {/* Bodegas origen → destino */}
        <div className="grid grid-cols-5 gap-3 items-end">
          <div className="col-span-2">
            <label htmlFor="transfermodal-from-warehouse" className={labelCls}>Bodega origen <span className="text-danger-solid">*</span></label>
            <select id="transfermodal-from-warehouse" {...register('from_warehouse', { required: 'Seleccione origen.' })} className={inputCls}>
              <option value="">Seleccionar...</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
            </select>
            {errors.from_warehouse && <p className={errorCls}>{String(errors.from_warehouse.message)}</p>}
          </div>
          <div className="flex justify-center pb-2">
            <ArrowRight size={20} className="text-content-subtle" />
          </div>
          <div className="col-span-2">
            <label htmlFor="transfermodal-to-warehouse" className={labelCls}>Bodega destino <span className="text-danger-solid">*</span></label>
            <select id="transfermodal-to-warehouse" {...register('to_warehouse', { required: 'Seleccione destino.' })} className={inputCls}>
              <option value="">Seleccionar...</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
            </select>
            {errors.to_warehouse && <p className={errorCls}>{String(errors.to_warehouse.message)}</p>}
          </div>
        </div>

        {/* Datos de Transporte — obligatorios para Guía de Despacho */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 rounded-lg space-y-3">
          <p className="text-xs text-blue-700 dark:text-blue-300 font-semibold flex items-center gap-1.5">
            <Truck size={14} /> Datos de transporte — Guía de Despacho (DTE 52)
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="transfermodal-carrier-name" className={labelCls}>Transportista <span className="text-danger-solid">*</span></label>
              <input id="transfermodal-carrier-name" {...register('carrier_name', { required: 'Obligatorio para GD.' })} placeholder="Ej: Transportes Sur SpA" className={inputCls} />
              {errors.carrier_name && <p className={errorCls}>{String(errors.carrier_name.message)}</p>}
            </div>
            <TextField
              dense
              label="RUT Transportista"
              placeholder="76.XXX.XXX-X"
              inputProps={register('carrier_tax_id')}
            />
            <div>
              <label htmlFor="transfermodal-driver-name" className={labelCls}>Chofer <span className="text-danger-solid">*</span></label>
              <input id="transfermodal-driver-name" {...register('driver_name', { required: 'Obligatorio para GD.' })} placeholder="Nombre completo del chofer" className={inputCls} />
              {errors.driver_name && <p className={errorCls}>{String(errors.driver_name.message)}</p>}
            </div>
            <div>
              <label htmlFor="transfermodal-vehicle-plate" className={labelCls}>Patente vehículo <span className="text-danger-solid">*</span></label>
              <input id="transfermodal-vehicle-plate" {...register('vehicle_plate', { required: 'Obligatorio para GD.' })} placeholder="Ej: ABCD-12" className={inputCls} style={{ textTransform: 'uppercase' }} />
              {errors.vehicle_plate && <p className={errorCls}>{String(errors.vehicle_plate.message)}</p>}
            </div>
          </div>
        </div>

        {/* Líneas */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls}>Productos a transferir</label>
            <button type="button" onClick={addLine} disabled={!fromWarehouseId}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-40">
              <Plus size={12} /> Agregar producto
            </button>
          </div>

          {!fromWarehouseId && (
            <p className="text-xs text-content-subtle py-4 text-center">Seleccione bodega origen para ver productos disponibles.</p>
          )}

          {lines.map((line, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2">
              <div className="col-span-6">
                <select aria-label="Seleccionar producto"
                  value={line.product_id}
                  onChange={e => updateLine(idx, 'product_id', e.target.value)}
                  className={`${inputCls} text-xs`}
                >
                  <option value="">Seleccionar producto...</option>
                  {availableStock.map(s => (
                    <option key={s.product_id} value={s.product_id}>
                      {s.product_sku} — {s.product_name} (disp: {parseFloat(s.available_quantity).toLocaleString('es-CL')})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <input aria-label="Cantidad a transferir"
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  max={line.available}
                  placeholder="Cantidad"
                  value={line.quantity}
                  onChange={e => updateLine(idx, 'quantity', e.target.value)}
                  className={`${inputCls} text-xs`}
                />
              </div>
              <div className="col-span-2 text-xs text-content-subtle pb-2">
                Disp: {parseFloat(line.available).toLocaleString('es-CL')}
              </div>
              <div className="col-span-1 pb-2">
                <button type="button" onClick={() => removeLine(idx)} className="p-1 text-danger-solid hover:text-danger-content">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}

          {lines.length === 0 && fromWarehouseId && (
            <p className="text-xs text-content-subtle py-3 text-center border border-dashed border-divider rounded-lg">
              Agregue productos a transferir.
            </p>
          )}
        </div>

        <TextField
          dense
          label="Notas"
          multiline
          rows={2}
          placeholder="Observaciones..."
          inputProps={register('notes')}
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-divider">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">Cancelar</button>
          <button type="submit" disabled={isPending || lines.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            Crear transferencia
          </button>
        </div>
      </form>
    </Modal>
  )
}
